// Call prepare/step1 with the first workspace audience id and render the form using MultiStepFlowFormv2

import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { useCompanyValues } from '@/providers/CompanyProvider';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useState } from 'react';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { showToast } from '@/features/toasts/toastsSlice';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { useAppDispatch } from '@/reducers';
import { ToastType } from '@/models/toast';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RouteConstants } from '@/features/router/RouteConstants';
import { WHATSAPP_TEMPLATE_CONNECTOR_FLOW_NAME } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/constants';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const WhatsappTemplateWorkaround = () => {
  const { id: workspaceId } = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  // Call connectorflow endpoint and parse connector_instance_id and whatsapp_connector_id
  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const whatsappConnectorFlow = connectorFlows?.find((flow) => flow.name === WHATSAPP_TEMPLATE_CONNECTOR_FLOW_NAME);
  const whatsappConnectorFlowId = whatsappConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === whatsappConnectorFlow?.connectorId)?.id;

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  // Fetch the prepare data when we have the required IDs
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: whatsappConnectorFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      // const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!whatsappConnectorFlowId && !!audienceId) {
      fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, whatsappConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  type CreateTemplateOpts = {
    flowParams?: FlowFormData;
    flowMappings?: FlowFormData;
    /** When true, skip mappings-form validation (flow finished without a mappings UI). */
    fromFlowComplete?: boolean;
  };

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const mappingsUiRequired = !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;
    if (!opts?.fromFlowComplete && mappingsUiRequired && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    const instance = instances?.find((inst) => inst.connectorId === whatsappConnectorFlow?.connectorId);
    if (!instance) {
      const message = String(t('campaign:noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('campaign:missingAudience.toast'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }
    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: whatsappConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId,
      flowParams,
      flowMappings,
    });
    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: 'Template created successfully',
          type: ToastType.SUCCESS,
        })
      );
      // TODO: Redirect to the template list page
      history.push(RouteConstants.whatsappTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('campaign:flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.whatsappTemplates),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void handleCreateTemplate(),
    disabled: !isMappingsFormValid,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const multiStepFlowFormProps = {
    id: 'template-workaround-form',
    showTemplatePreview: true,
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: whatsappConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      setMultiStepsCompleted(true);
      const hasMappingsUi = !!mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;
      if (!hasMappingsUi) {
        void handleCreateTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
      }
    },
  };

  const hasMappingsUi =
    !!multiStepMappingsConfig?.schema?.properties && Object.keys(multiStepMappingsConfig.schema.properties).length > 0;

  const isConnectorLoading = isConnectorFlowsLoading || isConnectorInstancesLoading;
  const isConnectorReady = !!connectorInstanceId && !!whatsappConnectorFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;
  const isTemplateLoading = isAudienceLoading || isConnectorLoading || isPreparePending;

  if (isAudienceLoading) {
    return (
      <div className='space-y-4'>
        <div>
          <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
        </div>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <div>
          <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
        </div>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
      </div>
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm {...multiStepFlowFormProps} />
          ) : (
            <p className='text-sm text-gray-600'>
              {isTemplateLoading ? t('campaign:whatsappTemplates.templateLoadingMessage') : t('campaign:whatsappTemplates.failedToLoadTemplate')}
            </p>
          )}
        </ErrorBoundary>
      )}
      {multiStepsCompleted && hasMappingsUi ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
